/**
 * @file Hand-over ("kiosk") mode — the PIN prompt that starts it and the guard that ends it.
 * @module components/forms/HandoverExitGuard
 *
 * F10 of ClickUp 86d3t4ev9. Two components, one file, because they are two halves of one
 * exchange and neither is meaningful without the other:
 *
 *   • `HandoverStartModal` — the PIN prompt. Lives in the clinical app (Forms tab, Forms In
 *     Tray). Confirms the practitioner means it, captures the PIN, records the audit event,
 *     then navigates THIS tab to the patient form.
 *   • `HandoverExitGuard` (default) — the corner "Return to clinic" affordance, the unlock
 *     dialog, the Back trap and the reload warning. Lives on `/patient/form`.
 *
 * ═══ THIS IS A SOFT GUARD ════════════════════════════════════════════════════════════════
 * Read the header of `utils/forms/handoverSession.ts` before changing anything here. The
 * short version: there is one browser session and nothing here scopes it, so a patient who
 * opens a new tab reaches the full clinical app. The UI says so at hand-over time, on
 * purpose — do not soften that copy into a guarantee the code does not enforce.
 *
 * ═══ WHY THERE IS NO SEPARATE "KIOSK RENDERER" ═══════════════════════════════════════════
 * `/patient/form` already renders OUTSIDE `<Layout>` — no sidebar, no topbar, no messaging
 * dock — and `App.tsx` suppresses `<XoeWidget />` on this route while `mode=handover`. The
 * chrome-free view is the patient link's existing view. Hand-over is that URL plus this
 * guard, not a second renderer that would drift from the first.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Input, Modal, Space, Typography } from "antd";
import { LockOutlined, LogoutOutlined } from "@ant-design/icons";

import { AssignedForm, getAssignedForms, startFormHandover } from "../../api/formApi";
import { buildPatientFormLink } from "../../utils/forms/patientFormLink";
import {
	HANDOVER_MAX_ATTEMPTS,
	HANDOVER_MODE,
	HANDOVER_MODE_PARAM,
	HANDOVER_PIN_LENGTH,
	HandoverSession,
	clearHandoverSession,
	enterHandover,
	exitHandover,
	isHandoverExiting,
	readHandoverSession,
	startHandoverSession,
	verifyHandoverPin,
} from "../../utils/forms/handoverSession";

const { Paragraph, Text } = Typography;

/** Digits only, capped at the PIN length — a paste of "12-34" becomes "1234", not a reject. */
const onlyPin = (raw: string): string => raw.replace(/\D/g, "").slice(0, HANDOVER_PIN_LENGTH);

/**
 * Shake keyframes, rendered with the dialog rather than added to `global.css`.
 *
 * The whole feature is then readable in one file and costs the other 100+ screens nothing —
 * this `<style>` only exists while a hand-over is actually in progress.
 */
const SHAKE_CSS = `
@keyframes mg-handover-shake {
	0%, 100% { transform: translateX(0); }
	20% { transform: translateX(-8px); }
	40% { transform: translateX(8px); }
	60% { transform: translateX(-5px); }
	80% { transform: translateX(5px); }
}
.mg-handover-shake { animation: mg-handover-shake 320ms ease-in-out; }
`;

// ─── Entry: the PIN prompt ────────────────────────────────────────────────────────────────

export interface HandoverStartModalProps {
	open: boolean;
	/** The patient-typed assignment being handed over. */
	assignedForm: AssignedForm;
	/** Shown in the confirmation copy. Optional — the tray row may not carry a patient. */
	patientName?: string;
	onCancel: () => void;
}

export const HandoverStartModal: React.FC<HandoverStartModalProps> = ({
	open,
	assignedForm,
	patientName,
	onCancel,
}) => {
	const [pin, setPin] = useState("");
	const [confirmPin, setConfirmPin] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (open) {
			setPin("");
			setConfirmPin("");
			setBusy(false);
			setError(null);
		}
	}, [open]);

	const handOver = async () => {
		if (pin.length !== HANDOVER_PIN_LENGTH) {
			setError(`Enter a ${HANDOVER_PIN_LENGTH}-digit PIN.`);
			return;
		}
		if (pin !== confirmPin) {
			setError("The two PINs do not match.");
			return;
		}

		setBusy(true);
		setError(null);
		try {
			// The tray listing deliberately does NOT carry `access_token` (it is a cross-patient
			// list and the token is a capability), while the patient's own Forms tab does. Fetch
			// it for this one patient only when it is missing — the same fallback the reminder
			// action uses.
			let accessToken = assignedForm.access_token;
			if (!accessToken) {
				try {
					const assignments = await getAssignedForms(assignedForm.patient_id);
					accessToken = assignments.find((a) => a.id === assignedForm.id)?.access_token;
				} catch {
					accessToken = undefined;
				}
			}

			// Audit FIRST, and abort the whole hand-over if it fails. `form.handover_started` is
			// the only record an abandoned hand-over leaves behind, and a chrome-free device with
			// no audit row is worse than a hand-over that visibly did not start.
			await startFormHandover(assignedForm.id);

			// Only now arm the guard: a failed write here means no PIN on the way out, so the
			// device must not be handed over.
			const armed = startHandoverSession({
				assignedFormId: assignedForm.id,
				pin,
				returnPath: `${window.location.pathname}${window.location.search}`,
			});
			if (!armed) {
				setError("This browser is blocking session storage, so the exit PIN cannot be held. Hand-over cancelled.");
				setBusy(false);
				return;
			}

			// Reuse the one link builder (it owns the "omit `&t=` entirely when absent" rule),
			// but force same-origin: `VITE_FRONTEND_BASE_URL` is a configured value and a
			// same-tab navigation to another origin would strand the practitioner.
			const link = new URL(buildPatientFormLink(assignedForm.id, accessToken), window.location.origin);
			enterHandover(`${link.pathname}${link.search}&${HANDOVER_MODE_PARAM}=${HANDOVER_MODE}`);
		} catch {
			clearHandoverSession();
			setError("Could not start hand-over. Nothing was handed over — try again.");
			setBusy(false);
		}
	};

	return (
		<Modal
			open={open}
			title="Hand this device to the patient"
			onCancel={onCancel}
			closable={!busy}
			maskClosable={!busy}
			destroyOnClose
			footer={[
				<Button key="cancel" onClick={onCancel} disabled={busy}>Cancel</Button>,
				<Button key="ok" type="primary" onClick={handOver} loading={busy} data-testid="handover-start-confirm">
					Hand to patient
				</Button>,
			]}
		>
			<div data-testid="handover-pin-prompt" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
				<Paragraph style={{ marginBottom: 0 }}>
					{patientName
						? `${patientName} will complete this form on this device.`
						: "The patient will complete this form on this device."}{" "}
					The clinical app is replaced by the form until you enter the PIN you set below.
				</Paragraph>

				{/*
					Deliberately blunt, and deliberately not a promise. The clinical session stays
					signed in on this browser; nothing here scopes or shortens it. Saying so here is
					the difference between an informed hand-over and a false sense of safety.
				*/}
				<Alert
					type="warning"
					showIcon
					message="This hides the clinical app — it does not sign you out"
					description={
						"Your session stays active on this browser. Someone who opens a new tab or switches apps "
						+ "can still reach it. Stay with the patient and do not leave the device unattended."
					}
				/>

				<div>
					<Text strong>Exit PIN</Text>
					<Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
						{HANDOVER_PIN_LENGTH} digits. You will need it to get back — after{" "}
						{HANDOVER_MAX_ATTEMPTS} wrong attempts the only way out is a full logout.
					</Paragraph>
					<Space direction="vertical" style={{ width: "100%" }} size={8}>
						<Input
							data-testid="handover-start-pin"
							aria-label="Exit PIN"
							type="password"
							inputMode="numeric"
							autoComplete="off"
							placeholder="••••"
							maxLength={HANDOVER_PIN_LENGTH}
							value={pin}
							disabled={busy}
							onChange={(e) => setPin(onlyPin(e.target.value))}
						/>
						<Input
							data-testid="handover-start-pin-confirm"
							aria-label="Confirm exit PIN"
							type="password"
							inputMode="numeric"
							autoComplete="off"
							placeholder="Confirm"
							maxLength={HANDOVER_PIN_LENGTH}
							value={confirmPin}
							disabled={busy}
							onChange={(e) => setConfirmPin(onlyPin(e.target.value))}
							onPressEnter={handOver}
						/>
					</Space>
				</div>

				{error && <Alert type="error" showIcon message={error} data-testid="handover-start-error" />}
			</div>
		</Modal>
	);
};

// ─── Exit: the guard ──────────────────────────────────────────────────────────────────────

export interface HandoverExitGuardProps {
	/**
	 * The assignment currently on screen. The guard arms only when the stored session is for
	 * this assignment, so a `mode=handover` URL that nobody handed over from (a bookmark, a
	 * pasted link on another device) renders as an ordinary patient form with no dead exit
	 * button on it.
	 */
	assignedFormId?: number | null;
}

const HandoverExitGuard: React.FC<HandoverExitGuardProps> = ({ assignedFormId }) => {
	const [session, setSession] = useState<HandoverSession | null>(() => readHandoverSession());
	const [dialogOpen, setDialogOpen] = useState(false);
	const [pin, setPin] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [shake, setShake] = useState(0);

	const armed = !!session && (assignedFormId == null || session.assignedFormId === assignedFormId);
	const lockedOut = !!session?.lockedOut;

	// `armed` is the dependency, not `session`: the session object is rewritten on every failed
	// attempt, and re-registering the history trap each time would push a fresh entry per wrong
	// PIN.
	useEffect(() => {
		if (!armed) return;

		// One extra history entry of our own. A Back press then pops onto it instead of leaving
		// the form, and the handler below immediately replaces what it consumed.
		const trap = () => window.history.pushState({ mgHandover: true }, "", window.location.href);
		trap();

		const onPopState = () => {
			trap();
			setError(null);
			setDialogOpen(true);
		};

		// Reload / close. Standing down while `exitHandover` is in flight matters: without it,
		// every deliberate exit would raise the browser's "leave site?" prompt at the patient.
		const onBeforeUnload = (event: BeforeUnloadEvent) => {
			if (isHandoverExiting()) return undefined;
			event.preventDefault();
			event.returnValue = "";
			return "";
		};

		window.addEventListener("popstate", onPopState);
		window.addEventListener("beforeunload", onBeforeUnload);
		return () => {
			window.removeEventListener("popstate", onPopState);
			window.removeEventListener("beforeunload", onBeforeUnload);
		};
	}, [armed]);

	const attemptUnlock = useCallback(() => {
		const result = verifyHandoverPin(pin);
		setPin("");

		if (result === "unlocked") {
			const current = readHandoverSession();
			exitHandover(current?.returnPath || "/");
			return;
		}

		setSession(readHandoverSession());

		if (result === "no-session") {
			// Storage went away underneath us. Nothing left to verify against, so the honest
			// thing is to stop pretending there is a guard.
			setError(null);
			setDialogOpen(false);
			return;
		}

		setShake((n) => n + 1);
		setError(
			result === "locked"
				? null
				: `Incorrect PIN. ${HANDOVER_MAX_ATTEMPTS - (readHandoverSession()?.attempts ?? 0)} attempts left.`,
		);
	}, [pin]);

	if (!armed) return null;

	return (
		<>
			<style>{SHAKE_CSS}</style>

			{/*
				Always mounted, never dismissable — the patient cannot hide it and the practitioner
				always has a way back. Top-right rather than bottom-right so it never lands on the
				wizard's Next/Submit controls on a 768px-wide tablet; `Form.tsx` pads the top of the
				page in hand-over mode so it never lands on the form card either.
			*/}
			<Button
				data-testid="handover-exit-button"
				icon={<LockOutlined />}
				onClick={() => { setError(null); setDialogOpen(true); }}
				size="small"
				style={{ position: "fixed", top: 12, right: 12, zIndex: 1100 }}
			>
				Return to clinic
			</Button>

			<Modal
				open={dialogOpen}
				title={lockedOut ? "Exit locked" : "Return to the clinical app"}
				closable={false}
				maskClosable={false}
				keyboard={false}
				width={360}
				footer={
					lockedOut
						? [
							<Button
								key="logout"
								type="primary"
								danger
								icon={<LogoutOutlined />}
								data-testid="handover-unlock-logout"
								onClick={() => exitHandover("/logout")}
							>
								Log out
							</Button>,
						]
						: [
							<Button key="back" onClick={() => { setPin(""); setError(null); setDialogOpen(false); }}>
								Back to form
							</Button>,
							<Button key="unlock" type="primary" onClick={attemptUnlock} data-testid="handover-unlock-submit">
								Unlock
							</Button>,
						]
				}
			>
				<div data-testid="handover-unlock-dialog">
					{lockedOut ? (
						<Alert
							type="error"
							showIcon
							message={`${HANDOVER_MAX_ATTEMPTS} incorrect PINs`}
							description={
								"This device is locked out of the exit. Logging out is the only way back, and it will "
								+ "end the clinical session on this browser."
							}
						/>
					) : (
						<Space direction="vertical" style={{ width: "100%" }} size={8}>
							<Text type="secondary">Enter the PIN set when this device was handed over.</Text>
							<div key={shake} className={shake > 0 ? "mg-handover-shake" : undefined}>
								<Input
									data-testid="handover-unlock-pin"
									aria-label="Exit PIN"
									type="password"
									inputMode="numeric"
									autoComplete="off"
									autoFocus
									placeholder="••••"
									maxLength={HANDOVER_PIN_LENGTH}
									status={error ? "error" : undefined}
									value={pin}
									onChange={(e) => setPin(onlyPin(e.target.value))}
									onPressEnter={attemptUnlock}
								/>
							</div>
							{error && <Text type="danger" style={{ fontSize: 12 }}>{error}</Text>}
						</Space>
					)}
				</div>
			</Modal>
		</>
	);
};

export default HandoverExitGuard;
